// "Make the amount" questions (21 Oct 2026, Year 3): the student taps coins
// and notes (or base-ten blocks) into a tray to make an amount or a number.
//
// What is sent, and how its answer key is stored, depends on the question:
//   * accept "exact" (these pieces, e.g. "with the fewest coins"): one
//     "<piece>=<how many>" for each piece used ("$2=1", "50c=3",
//     "hundred=2"), sorted;
//   * accept "total" (any pieces that make it): the one entry
//     "total=<amount>", the tray's total in dollars for money
//     ("total=$3.7") or as a whole number for blocks ("total=347").
// The two are marked by comparing those strings.
import { centsOf, MONEY_VALUES, type MoneyValue } from './practice-money-data';

export const BLOCK_PIECES = ['thousand', 'hundred', 'ten', 'one'] as const;
export type BlockPiece = (typeof BLOCK_PIECES)[number];
export type BuildPiece = MoneyValue | BlockPiece;

export type BuildQuestion = {
  kind: 'money' | 'blocks';
  // The pieces to choose from, biggest first.
  pieces: BuildPiece[];
  accept: 'exact' | 'total';
};

// More pieces than this in the tray cannot be told apart on a phone.
export const MAX_TRAY = 30;

const BLOCK_VALUES: Record<BlockPiece, number> = {
  thousand: 1000,
  hundred: 100,
  ten: 10,
  one: 1,
};

function isMoneyPiece(value: unknown): value is MoneyValue {
  return (
    typeof value === 'string' &&
    (MONEY_VALUES as readonly string[]).includes(value)
  );
}

function isBlockPiece(value: unknown): value is BlockPiece {
  return (
    typeof value === 'string' &&
    (BLOCK_PIECES as readonly string[]).includes(value)
  );
}

function isBuildPiece(value: unknown): value is BuildPiece {
  return isMoneyPiece(value) || isBlockPiece(value);
}

// A piece's value: cents for money, ones for blocks.
export function pieceValue(piece: BuildPiece): number {
  return isBlockPiece(piece) ? BLOCK_VALUES[piece] : centsOf(piece);
}

// The question's pieces and how it is marked, read defensively from the
// content (or from a bank question): all coins and notes, or all blocks, no
// piece twice, and accept "exact" or "total".
export function readBuild(content: unknown): BuildQuestion | null {
  if (typeof content !== 'object' || content === null) return null;
  const record = content as Record<string, unknown>;
  const { pieces, accept } = record;
  if (accept !== 'exact' && accept !== 'total') return null;
  if (!Array.isArray(pieces) || pieces.length < 1 || pieces.length > 11)
    return null;
  if (!pieces.every(isBuildPiece)) return null;
  if (new Set(pieces).size !== pieces.length) return null;
  const money = pieces.every(isMoneyPiece);
  if (!money && !pieces.every(isBlockPiece)) return null;
  return {
    kind: money ? 'money' : 'blocks',
    pieces: [...pieces].sort((a, b) => pieceValue(b) - pieceValue(a)),
    accept,
  };
}

// An amount as the entry writes it: dollars for money with no stray zeros
// (370 cents is "$3.7", 5 cents is "$0.05"), a whole number for blocks.
function totalText(kind: BuildQuestion['kind'], value: number): string {
  return kind === 'money' ? `$${String(value / 100)}` : String(value);
}

// How many of each piece, as sent. Null when nothing is in the tray or a
// piece is not one of the question's own.
export function buildEntries(
  question: BuildQuestion,
  counts: Partial<Record<BuildPiece, number>>,
): string[] | null {
  let pieces = 0;
  let total = 0;
  const entries: string[] = [];
  for (const [piece, count] of Object.entries(counts)) {
    if (count === 0) continue;
    if (!question.pieces.includes(piece as BuildPiece)) return null;
    if (!Number.isInteger(count) || count < 0) return null;
    pieces += count;
    total += pieceValue(piece as BuildPiece) * count;
    entries.push(`${piece}=${String(count)}`);
  }
  if (pieces === 0) return null;
  return question.accept === 'total'
    ? [`total=${totalText(question.kind, total)}`]
    : entries.sort();
}

// The answer key of a bank question: its answer (piece: how many), or for
// accept "total" its total (dollars for money). Null when the answer does
// not fit the question.
export function buildKey(
  question: BuildQuestion,
  answer: unknown,
  total: unknown,
): string[] | null {
  if (typeof answer !== 'object' || answer === null || Array.isArray(answer))
    return null;
  const counts: Partial<Record<BuildPiece, number>> = {};
  for (const [piece, count] of Object.entries(answer)) {
    if (!isBuildPiece(piece) || !question.pieces.includes(piece)) return null;
    if (typeof count !== 'number' || !Number.isInteger(count) || count < 0)
      return null;
    counts[piece] = count;
  }
  const entries = buildEntries(question, counts);
  if (entries === null || question.accept === 'exact') return entries;
  // The total the question names must be the answer's own total.
  if (typeof total !== 'number' || !Number.isFinite(total)) return null;
  const named =
    question.kind === 'money' ? Math.round(total * 100) : Math.round(total);
  return entries[0] === `total=${totalText(question.kind, named)}`
    ? entries
    : null;
}

// The practice screen keeps every answer as one piece of text: here the
// pieces in the tray, one on each line, in the order they were tapped in.
export function splitTray(
  question: BuildQuestion,
  value: string,
): BuildPiece[] {
  return value
    .split('\n')
    .filter((line): line is BuildPiece =>
      question.pieces.includes(line as BuildPiece),
    );
}

export function trayCounts(
  tray: BuildPiece[],
): Partial<Record<BuildPiece, number>> {
  const counts: Partial<Record<BuildPiece, number>> = {};
  for (const piece of tray) counts[piece] = (counts[piece] ?? 0) + 1;
  return counts;
}

// What is sent for the tray as it stands; null while it is empty.
export function buildOrderFromTray(
  question: BuildQuestion,
  value: string,
): string[] | null {
  return buildEntries(question, trayCounts(splitTray(question, value)));
}

// For the results pages: "$2=1" back into its piece and count, and
// "total=$3.7" or "total=347" into its total. Null for anything else.
export function parseBuildEntry(
  entry: unknown,
):
  | { piece: BuildPiece; count: number }
  | { total: number; money: boolean }
  | null {
  if (typeof entry !== 'string') return null;
  const total = /^total=(\$?)(\d+(?:\.\d+)?)$/.exec(entry);
  if (total) return { total: Number(total[2]), money: total[1] === '$' };
  const match = /^(.+)=(\d+)$/.exec(entry);
  if (!match || !isBuildPiece(match[1])) return null;
  return { piece: match[1], count: Number(match[2]) };
}

// "$3.70", "70c", or a whole number for blocks ("347").
export function formatBuildTotal(total: number, money: boolean): string {
  if (!money) return total.toLocaleString('en-AU');
  const cents = Math.round(total * 100);
  return cents < 100 ? `${String(cents)}c` : `$${(cents / 100).toFixed(2)}`;
}

// The name a screen reader gives a piece: "50c", "$2", "a ten".
export function pieceName(piece: BuildPiece): string {
  return isBlockPiece(piece) ? `a ${piece}` : piece;
}

export function isMoneyBuildPiece(piece: BuildPiece): piece is MoneyValue {
  return isMoneyPiece(piece);
}
